import { useEffect, useMemo, useState } from "react";
import { api, itemImage, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Heart, ListMusic, Plus, Check, Loader2, Replace, Search, Sparkles, Shuffle, Camera, X } from "lucide-react";
import { toast } from "sonner";

// ordered by how often they actually happen: everyday first, once-in-a-while last
export const OCCASIONS = ["Weekend", "Work", "Dinner", "Night out", "Date", "Rainy day", "Gym", "Travel day", "Interview", "Beach", "Festival", "Wedding"];
const GROUP_LABEL = { top: "Tops", bottom: "Bottoms", outerwear: "Outerwear", dress: "Dresses", shoes: "Shoes", accessory: "Accessories", other: "Other" };
const GROUP_ORDER = ["outerwear", "top", "dress", "bottom", "shoes", "accessory", "other"];

const useCloset = (open) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.listItems({}).then((r) => setItems(Array.isArray(r) ? r : r.items || [])).catch(() => setItems([]));
  }, [open]);
  return items;
};

/** Searchable grid of your closet, grouped by category. */
const ClosetPicker = ({ items, selected = [], onToggle, single = false, focusCategory, testId = "closet-picker" }) => {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(focusCategory || "all");
  useEffect(() => { if (focusCategory) setCat(focusCategory); }, [focusCategory]);
  const filtered = useMemo(() => (items || []).filter((i) => (cat === "all" || i.category === cat) && (!q || `${i.name} ${i.brand} ${i.primary_color} ${i.subtype}`.toLowerCase().includes(q.toLowerCase()))), [items, cat, q]);
  const cats = useMemo(() => GROUP_ORDER.filter((g) => (items || []).some((i) => i.category === g)), [items]);
  if (items === null) return <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-[3/4] rounded-xl" />)}</div>;
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[160px]"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your closet" className="pl-8 h-9" data-testid={`${testId}-search`} /></div>
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          <button onClick={() => setCat("all")} className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap ${cat === "all" ? "bg-foreground text-background" : "bg-secondary"}`}>All</button>
          {cats.map((g) => <button key={g} onClick={() => setCat(g)} className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap ${cat === g ? "bg-foreground text-background" : "bg-secondary"}`} data-testid={`${testId}-cat-${g}`}>{GROUP_LABEL[g]}</button>)}
        </div>
      </div>
      {filtered.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">Nothing here yet.</p>
        : <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[52vh] overflow-y-auto pr-1">
          {filtered.map((it) => {
            const on = selected.includes(it.id);
            return (
              <button key={it.id} onClick={() => onToggle(it)} className={`relative text-left rounded-xl overflow-hidden border transition-colors ${on ? "border-action ring-2 ring-action/40" : "border-border hover:border-foreground/40"}`} data-testid={`${testId}-item-${it.id}`}>
                <div className="aspect-[3/4] bg-secondary">{it.review?.needs_front_photo ? <div className="h-full w-full flex flex-col items-center justify-center gap-1 text-center px-2"><Camera className="h-4 w-4 text-muted-foreground" /><span className="text-[10px] font-medium leading-tight">Front photo needed</span></div> : <img src={itemImage(it)} alt={it.name} loading="lazy" className="h-full w-full object-cover" />}</div>
                <div className="p-1.5"><p className="text-[11px] font-medium truncate">{it.name || it.subtype}</p><p className="text-[10px] text-muted-foreground truncate">{it.size || it.brand || GROUP_LABEL[it.category]}</p></div>
                {on && <span className="absolute top-1.5 right-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-action text-action-foreground"><Check className="h-3 w-3" /></span>}
              </button>
            );
          })}
        </div>}
    </div>
  );
};

/** Click a piece in a look -> swap it for another piece from your closet. */
export const SwapPieceDialog = ({ outfit, piece, open, onOpenChange, onSwapped }) => {
  const items = useCloset(open);
  const [busy, setBusy] = useState(false);
  const choose = async (it) => {
    setBusy(true);
    try {
      const o = await api.swapOutfitItem(outfit.id, piece?.id || null, it.id);
      toast.success(piece ? `Swapped ${piece.name || "piece"} for ${it.name}` : `Added ${it.name}`);
      onSwapped?.(o); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not swap"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="swap-piece-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Replace className="h-5 w-5 text-action" /> {piece ? `Swap the ${piece.name || "piece"}` : "Add a piece"}</DialogTitle>
          <DialogDescription>{piece ? "Pick what should go in its place. The look re-renders with your change." : "Pick any piece to add to this look."}</DialogDescription>
        </DialogHeader>
        {busy ? <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div>
          : <ClosetPicker items={(items || []).filter((i) => !outfit.item_ids?.includes(i.id))} onToggle={choose} single focusCategory={piece?.category} testId="swap-picker" />}
      </DialogContent>
    </Dialog>
  );
};

/** Heart toggle. Colour = good (green) when saved — "this is a keeper". */
export const HeartButton = ({ outfit, onChange, size = "sm", className = "" }) => {
  const [fav, setFav] = useState(!!outfit?.favorite);
  const [pop, setPop] = useState(false);
  useEffect(() => setFav(!!outfit?.favorite), [outfit?.favorite, outfit?.id]);
  const toggle = async (e) => {
    e?.stopPropagation?.();
    try {
      const r = await api.favoriteOutfit(outfit.id);
      setFav(r.favorite); setPop(true); setTimeout(() => setPop(false), 400);
      toast.success(r.favorite ? "Saved to your favourites" : "Removed from favourites");
      onChange?.(r.favorite);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
  };
  return (
    <button onClick={toggle} className={`inline-flex items-center justify-center rounded-full border transition-colors ${size === "lg" ? "h-11 w-11" : "h-9 w-9"} ${fav ? "bg-good text-good-foreground border-good" : "bg-background/85 backdrop-blur border-border hover:border-foreground/40"} ${className}`} aria-label={fav ? "Remove from favourites" : "Save to favourites"} aria-pressed={fav} data-testid={`outfit-heart-${outfit.id}`}>
      <Heart className={`${size === "lg" ? "h-5 w-5" : "h-4 w-4"} ${fav ? "fill-current" : ""} ${pop ? "animate-pulse-once" : ""}`} />
    </button>
  );
};

/** "Add to daylist" menu with inline create. */
export const AddToDaylist = ({ outfit, variant = "outline" }) => {
  const [lists, setLists] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const load = () => api.daylists().then(setLists).catch(() => setLists([]));
  const add = async (dl) => {
    try { await api.updateDaylist(dl.id, { add_outfit_id: outfit.id }); toast.success(`Added to “${dl.name}”`); }
    catch { toast.error("Could not add"); }
  };
  const create = async () => {
    if (!name.trim()) return;
    try { const dl = await api.createDaylist({ name: name.trim(), outfit_ids: [outfit.id] }); toast.success(`Created “${dl.name}”`); setName(""); setCreating(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size="sm" className="rounded-full" data-testid={`outfit-daylist-${outfit.id}`}><ListMusic className="h-4 w-4 mr-1.5" /> Daylist</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 glass" data-testid="daylist-menu">
        <p className="px-2 py-1.5 text-xs text-muted-foreground">Add this look to a daylist</p>
        {lists === null ? <div className="px-2 py-2"><Skeleton className="h-6" /></div>
          : lists.length === 0 ? <p className="px-2 py-2 text-xs text-muted-foreground">No daylists yet — make your first one below.</p>
          : lists.map((dl) => <DropdownMenuItem key={dl.id} onClick={() => add(dl)} data-testid={`daylist-menu-${dl.id}`}><ListMusic className="h-4 w-4 mr-2 text-muted-foreground" /> <span className="flex-1 truncate">{dl.name}</span><span className="font-mono-plex text-[10px] text-muted-foreground">{dl.count}</span></DropdownMenuItem>)}
        <DropdownMenuSeparator />
        {creating ? (
          <div className="p-2 flex gap-1.5" onKeyDown={(e) => e.stopPropagation()}>
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="e.g. Lisbon trip" className="h-8 text-sm" data-testid="daylist-new-name" />
            <Button size="sm" onClick={create} className="h-8 rounded-full" data-testid="daylist-new-save"><Check className="h-4 w-4" /></Button>
          </div>
        ) : <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setCreating(true); }} data-testid="daylist-menu-new"><Plus className="h-4 w-4 mr-2" /> New daylist</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Build a look from a brief: what is it for (or random)? start from any pieces? The stylist fills in the rest.
    No closet grid up front and no name - you see the look first and name it only if it's a keeper. */
export const BuildLookDialog = ({ open, onOpenChange, onCreated, initialOccasion }) => {
  const items = useCloset(open);
  const [occasion, setOccasion] = useState(initialOccasion || "");
  const [custom, setCustom] = useState("");
  const [random, setRandom] = useState(false);
  const [anchorMode, setAnchorMode] = useState(false);
  const [anchors, setAnchors] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setOccasion(initialOccasion || ""); setCustom(""); setRandom(false); setAnchorMode(false); setAnchors([]); } }, [open, initialOccasion]);
  const toggle = (it) => setAnchors((s) => (s.includes(it.id) ? s.filter((x) => x !== it.id) : [...s, it.id]));
  const chosen = (items || []).filter((i) => anchors.includes(i.id));
  const finalOccasion = (custom.trim() || occasion || "").trim();
  const ready = random || !!finalOccasion;
  const build = async () => {
    setBusy(true);
    try {
      const o = await api.buildLook({ occasion: finalOccasion || null, anchor_ids: anchors, random });
      toast.success(random ? "Surprise look coming up - rendering it on your twin" : `Built a ${finalOccasion} look - rendering it on your twin`);
      onCreated?.(o); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the look"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="build-look-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-action" /> Build a look</DialogTitle>
          <DialogDescription>Two quick questions. Your stylist picks the pieces, you see it on your twin, and you name it only if it's a keeper.</DialogDescription>
        </DialogHeader>

        {/* Q1: what's it for? */}
        <section className="space-y-2.5" data-testid="build-step-occasion">
          <p className="text-sm font-medium">1 · What are you doing, or what's the look for?</p>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => { setRandom(!random); if (!random) { setOccasion(""); setCustom(""); } }} className={`px-3 py-1.5 rounded-full text-xs border inline-flex items-center gap-1.5 transition-colors ${random ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`} data-testid="build-random"><Shuffle className="h-3.5 w-3.5" /> Just randomise it</button>
            {OCCASIONS.map((o) => <button key={o} type="button" onClick={() => { setOccasion(occasion === o ? "" : o); setCustom(""); setRandom(false); }} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${occasion === o && !custom ? "bg-action text-action-foreground border-action" : "border-border hover:border-foreground/40"}`} data-testid={`build-occasion-${o.toLowerCase().replace(/\s+/g, "-")}`}>{o}</button>)}
          </div>
          <Input value={custom} onChange={(e) => { setCustom(e.target.value); if (e.target.value) { setOccasion(""); setRandom(false); } }} placeholder="Or type it: school, gallery opening, first day at work…" className="h-10" data-testid="build-occasion-custom" />
        </section>

        {/* Q2: start from anything? */}
        <section className="space-y-2.5 mt-2" data-testid="build-step-anchor">
          <p className="text-sm font-medium">2 · Want to start the look from anything?</p>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => { setAnchorMode(false); setAnchors([]); }} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${!anchorMode ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`} data-testid="build-anchor-none">No, build it all for me</button>
            <button type="button" onClick={() => setAnchorMode(true)} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${anchorMode ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`} data-testid="build-anchor-yes">Yes, start from these pieces…</button>
          </div>
          {anchorMode && (
            <div className="rounded-2xl border border-border p-3 space-y-3" data-testid="build-anchor-picker">
              <div className="flex flex-wrap items-center gap-1.5 min-h-[2.5rem]">
                {chosen.length === 0 ? <p className="text-xs text-muted-foreground">Pick the pants, the jacket, a shirt - one piece or a few. The rest gets built around them.</p>
                  : chosen.map((i) => <button key={i.id} type="button" onClick={() => toggle(i)} className="inline-flex items-center gap-1.5 rounded-full border border-border pl-1 pr-2 h-8 text-xs hover:bg-secondary" title={`Remove ${i.name}`} data-testid={`build-anchor-chip-${i.id}`}><span className="h-6 w-6 rounded-full overflow-hidden bg-secondary inline-block"><img src={itemImage(i)} alt="" className="h-full w-full object-cover" /></span>{i.name}<X className="h-3 w-3 text-muted-foreground" /></button>)}
              </div>
              <ClosetPicker items={items} selected={anchors} onToggle={toggle} testId="build-picker" />
            </div>
          )}
        </section>

        <Button onClick={build} disabled={busy || !ready} className="w-full rounded-full mt-3" data-testid="build-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> {random ? "Surprise me" : anchors.length ? `Build the rest around ${anchors.length} piece${anchors.length === 1 ? "" : "s"}` : "Build the look"}</>}
        </Button>
        {!ready && <p className="text-[11px] text-muted-foreground text-center">Pick what it's for (or randomise) to continue.</p>}
      </DialogContent>
    </Dialog>
  );
};

export const OccasionBadge = ({ occasion }) => occasion ? <Badge variant="secondary" className="rounded-full text-[10px] capitalize" data-testid="outfit-occasion">{occasion}</Badge> : null;
